import { useMutation } from '@tanstack/react-query';

import { WRITING_ASSISTANT_SUGGESTION } from '@app/constants/endpoints';
import { postData } from '@app/hooks/react-query-helpers';

// Mirrors server/constants/writing-assistant.js — keep in lock-step.
export const WRITING_ASSISTANT_ACTION = {
  EXPAND: 'expand',
  CONCISE: 'concise',
  REPHRASE: 'rephrase',
  TONE: 'tone',
} as const;
export type WritingAssistantAction =
  (typeof WRITING_ASSISTANT_ACTION)[keyof typeof WRITING_ASSISTANT_ACTION];
export const WRITING_ASSISTANT_ACTIONS: WritingAssistantAction[] =
  Object.values(WRITING_ASSISTANT_ACTION);
export const WRITING_ASSISTANT_ACTION_LABEL_KEYS = {
  [WRITING_ASSISTANT_ACTION.EXPAND]: 'expand',
  [WRITING_ASSISTANT_ACTION.CONCISE]: 'shorten',
  [WRITING_ASSISTANT_ACTION.REPHRASE]: 'rephrase',
  [WRITING_ASSISTANT_ACTION.TONE]: 'changeTone',
} as const satisfies Record<WritingAssistantAction, string>;

// Wire values — must match the server + Fabric TONE enum; display labels are localized separately.
export const WRITING_ASSISTANT_TONES = [
  'Empathetic',
  'Professional',
  'Friendly',
  'Neutral',
] as const;
export type WritingAssistantTone = (typeof WRITING_ASSISTANT_TONES)[number];

export const WRITING_ASSISTANT_STATUS = {
  READY: 'ready',
  DISABLED: 'disabled',
  ERROR: 'error',
} as const;
export type WritingAssistantStatus =
  (typeof WRITING_ASSISTANT_STATUS)[keyof typeof WRITING_ASSISTANT_STATUS];

export interface WritingAssistantResponse {
  status: WritingAssistantStatus;
  suggestion?: string;
  // Not persisted server-side; AI feedback attributes a rating by it.
  suggestionId?: string;
}

export interface WritingAssistantRequest {
  draft: string;
  action: WritingAssistantAction;
  tone?: WritingAssistantTone;
}

/**
 * Stateless suggestion call. The server proxies to Fabric and always resolves
 * 200 with a `status` ('ready' | 'disabled' | 'error'); only `ready` carries a
 * `suggestion`. A rejected promise means an HTTP-level failure (restriction,
 * validation) — callers treat both non-`ready` and rejection as an error state.
 */
export const useWritingAssistantSuggestion = (jobId: string) =>
  useMutation({
    mutationFn: ({ draft, action, tone }: WritingAssistantRequest) =>
      postData(WRITING_ASSISTANT_SUGGESTION(jobId), {
        draft,
        action,
        ...(tone ? { tone } : {}),
      }) as Promise<WritingAssistantResponse>,
  });
